import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from 'moment';
import cloneDeep from "lodash.clonedeep";
import { useEventContext } from "./EventContext";
import { Event } from "../../../cp/components/Comet";
import { quizGetList } from "../../../player/js/api";
import { EVENT_TYPE_RESTORED } from "../../../player/js/CometProvider/GoCometProvider";
import { useNotificationsContext } from "./NotificationsContext";
import { usePlayerContext } from "./PlayerContext";
import { ModalContextProvider } from "./ModalContext";
import { useStreamContext } from "./StreamContext";
import { useUserContext } from "./UserContext";
import { usePollsContext } from "./PollsContext";
import { fullscreenClose } from "../components/Player/FullScreen";
import QuizModal from "../components/QuizModal";

const checkQuizIsInProgress = (quiz) => {
    return quiz && quiz.modalStartAt
        && (!quiz.modalTimeout || moment.unix(quiz.modalStartAt + quiz.modalTimeout).isAfter(moment()))
};

export const QuizContext = createContext([]);

export const QuizProvider = ({ children, preview, pageName }) => {
    const { event } = useEventContext();
    const { addMessage } = useNotificationsContext();
    const { setFullScreen, fullScreen } = usePlayerContext();
    const { stream } = useStreamContext();
    const { participationType } = useUserContext();
    const { answeredPollIds } = usePollsContext();

    const [quiz, setQuiz] = useState(null);
    const [quizzes, setQuizzes] = useState(null);
    const [activeQuizzes, setActiveQuizzes] = useState([]);
    const [closed, setClosed] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const activeQuizKey = params.get('active-quiz-key');
    const activePollId = params.get('view-poll-id');

    const quizzesRef = useRef();
    const isAnswerUpdateRef = useRef(false);

    const quizActivePollIndex = (quiz) => quiz.polls.findIndex(({ id }) => -1 === answeredPollIds.indexOf(id));

    const getQuizzes = async () => {
        setIsLoading(true);
        try {
            const _quizzes = await quizGetList(event.eventKey);
            setQuizzes(_quizzes);
        } catch (e) { }
        setIsLoading(false);
    };

    const onCloseQuiz = (id) => {
        if (!quiz || preview) return;
        setQuiz(null);
        navigate(window.location.pathname);
        if (id) {
            setClosed(prev => [...prev, id]);
        }
    };

    useEffect(() => {
        getQuizzes();
        // if (!preview) {
        //     navigate(window.location.pathname);
        // }
    }, []);

    useEffect(() => {
        if (!quizzes || activePollId) return;

        if (!activeQuizKey) {
            onCloseQuiz();
            // return;
        }

        if (isAnswerUpdateRef.current) {
            isAnswerUpdateRef.current = false;
            return;
        }

        const activeQuiz = quizzes.find(({ quizKey }) => quizKey === activeQuizKey);

        if ((!activeQuiz || (activeQuiz.id === quiz?.id && !activeQuiz.modalStartAt)
            && quizzesRef.current !== quizzes)
            || activeQuiz && !!activeQuiz.modalTimeout && !activeQuiz.modalStartAt
        ) {
            onCloseQuiz();
        } else {
            setQuiz(activeQuiz);
        }

        quizzesRef.current = quizzes;
    }, [activeQuizKey, activePollId, quiz, quizzes]);

    useEffect(() => {
        if (Array.isArray(quizzes) && !activePollId && !preview) {

            const _activeQuizzes = quizzes.filter(quiz => quiz.enabled
                && (!quiz.participationType || quiz.participationType === participationType)
                && (!quiz.displayPage?.length || (pageName && quiz.displayPage?.includes(pageName)))
                && (!quiz.displayStreams?.length || quiz.displayStreams?.includes(String(stream.id)))
                && !closed.includes(quiz.id) && checkQuizIsInProgress(quiz)
                && !!quiz.polls[quizActivePollIndex(quiz)]).sort((a, b) => a.modalStartAt - b.modalStartAt
                );

            const activeQuiz = _activeQuizzes[0];

            setActiveQuizzes(_activeQuizzes);

            if (activeQuiz && !activeQuizKey) {
                navigate(`${window.location.pathname}?active-quiz-key=${activeQuiz.quizKey}`);
            }

            if (_activeQuizzes.length > activeQuizzes.length && fullScreen) {
                addMessage({
                    title: 'quiz.toast.activeQuiz.title',
                    buttonTitle: 'quiz.toast.activeQuiz.action',
                    callback: () => {
                        setFullScreen(null);
                        fullscreenClose();
                    },
                });
            }
        }
    }, [quizzes, pageName, stream, participationType, closed]);

    const updateQuizzes = (quizzes, quiz) => {
        if (!quizzes) {
            return null;
        }
        const index = quizzes.findIndex(({ id }) => id === quiz.id);
        if (-1 === index) {
            return [...quizzes, quiz];
        } else {
            let _quizzes = quizzes.slice();
            const polls = cloneDeep(_quizzes[index]?.polls) || [];
            _quizzes[index] = Object.assign({}, _quizzes[index], quiz);

            _quizzes[index].polls = polls;
            quizzesRef.current = _quizzes;

            return _quizzes;
        }
    }

    const updateQuizzPolls = (quizzes, poll, action) => {
        if (!quizzes) {
            return null
        }
        const index = quizzes.findIndex(({ id }) => id === poll.quizId);
        if (-1 === index) {
            return quizzes;
        } else {
            let _quizzes = quizzes.slice();
            let polls = cloneDeep(quizzes[index]?.polls || []);
            if (action === 'update') {
                const pollIndex = polls.findIndex(({ id }) => id === poll.id);
                if (pollIndex < 0) {
                    polls = [...polls, poll]
                } else {
                    polls[pollIndex] = Object.assign({}, polls[pollIndex], poll);
                }
            } else {
                polls = polls.filter(({ id }) => id !== poll.id);
            }
            _quizzes[index].polls = polls;
            quizzesRef.current = _quizzes;

            return _quizzes;
        }
    }

    const updateQuizzPollAnswers = (quizzes, answer, action) => {
        if (!quizzes) {
            return null;
        }

        const index = quizzes.findIndex(quiz => quiz.polls?.find(({ id }) => id === answer.pollId));
        if (-1 === index) {
            return quizzes;
        } else {
            let _quizzes = quizzes.slice();
            let polls = cloneDeep(quizzes[index]?.polls || []);
            const pollIndex = polls.findIndex(({ id }) => id === answer.pollId);
            if (pollIndex >= 0) {
                let answers = cloneDeep(polls[pollIndex]?.answer || []);
                if (action === 'update') {
                    answers = answers.findIndex(({ id }) => id === answer.id) < 0 ? [...answers, answer] : answers.map(prev => prev.id === answer.id ? answer : prev);
                } else {
                    answers = answers.filter(({ id }) => id !== answer.id)
                }
                polls[pollIndex].answer = answers;
            }
            _quizzes[index].polls = polls;
            quizzesRef.current = _quizzes;
            isAnswerUpdateRef.current = true;

            return _quizzes;
        }
    }

    const onQuizUpdate = (quiz) => {
        setQuizzes((quizzes) => updateQuizzes(cloneDeep(quizzes), quiz));
    };

    const onQuizDelete = (quiz) => {
        setQuizzes((quizzes) => quizzes?.filter(({ id }) => id !== quiz.id));
    };

    const onPollUpdate = (poll) => {
        if (poll?.quizId) {
            setQuizzes((quizzes) => updateQuizzPolls(cloneDeep(quizzes), poll, 'update'));
        }
    }

    const onPollDelete = (poll) => {
        if (poll?.quizId) {
            setQuizzes((quizzes) => updateQuizzPolls(cloneDeep(quizzes), poll, 'delete'));
        }
    }

    const onPollAnswerUpdate = (answer) => {
        if (answer?.quizId) {
            setQuizzes((quizzes) => updateQuizzPollAnswers(cloneDeep(quizzes), answer, 'update'));
        }
    }

    const onPollAnswerDelete = (answer) => {
        if (answer?.pollId) {
            setQuizzes((quizzes) => updateQuizzPollAnswers(cloneDeep(quizzes), answer, 'delete'));
        }
    }

    const sortedQuizzes = useMemo(() => !quizzes ? quizzes : quizzes.slice().sort((a, b) => a.order - b.order), [quizzes]);

    const context = {
        quiz,
        quizzes: sortedQuizzes,
        onQuizUpdate,
        isLoading,
        onCloseQuiz,
        activeQuizKey,
    };

    return (
        <QuizContext.Provider value={context}>
            <Event event={"create:quiz"} handler={(quiz) => onQuizUpdate(quiz)} />
            <Event event={"update:quiz"} handler={(quiz) => onQuizUpdate(quiz)} />
            <Event event={"delete:quiz"} handler={(quiz) => onQuizDelete(quiz)} />
            <Event event={`create:poll`} handler={(poll) => onPollUpdate(poll)} />
            <Event event={`update:poll`} handler={(poll) => onPollUpdate(poll)} />
            <Event event={`delete:poll`} handler={(poll) => onPollDelete(poll)} />
            <Event event={`create:poll/answer`} handler={(answer) => onPollAnswerUpdate(answer)} />
            <Event event={`update:poll/answer`} handler={(answer) => onPollAnswerUpdate(answer)} />
            <Event event={`delete:poll/answer`} handler={(answer) => onPollAnswerDelete(answer)} />
            <Event event={EVENT_TYPE_RESTORED} handler={getQuizzes} />
            {children}
            {!preview &&
                <ModalContextProvider>
                    <QuizModal pageName={pageName} />
                </ModalContextProvider>
            }
        </QuizContext.Provider>
    );
};

export const useQuizContext = () => useContext(QuizContext);
